import { useCallback, useState } from "react";
import type { ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Maximize2 } from "lucide-react";
import {
  ResourceDrawer,
  type ResourceDrawerProps,
} from "@/components/portfolioAdmin/loanDetail/common/ResourceDrawer";

/**
 * A record read WITHOUT leaving the record you are standing on.
 *
 * <p>A specialist working a buyer's shortlist wants to look at a listing, not to
 * go to it: navigating away costs the buyer's tab, its scroll position and its
 * place in the queue, and the way back is the browser button. This renders the
 * listing's own body in a right-side drawer over the buyer, so the answer
 * arrives and the workspace survives.</p>
 *
 * <p><b>It is a thin wrapper over the existing {@link ResourceDrawer}</b>, which
 * already owns Escape-to-close, backdrop dismissal, the scroll lock and the
 * header slot. Nothing about that behaviour is reimplemented here; this adds the
 * two things a RECORD drawer needs on top of a resource one — the standard
 * width for an entity body, and the way out to the record's own address.</p>
 *
 * <p><b>The way out is not a fallback, it is the point.</b> A drawer is a place
 * to look, and "Open full page" is the route to every write the drawer does
 * not offer (Layout Standard §7.4a item 3). What it MAY offer, since v1.8 of
 * that standard, is the record's own status control in this HEADER — through
 * the {@link EntityRecordDrawerProps.actions} slot — on two conditions: the
 * control is the record page's own component, same mutation and same
 * permission gate, never a second form; and the drawer's title names the
 * record being changed, which is what answers "doing it to the wrong record".
 * The slot is opt-in; a drawer that passes nothing renders the same header it
 * always did, one wrapper `<div>` deeper around the link.</p>
 *
 * <p><b>Deliberately NOT built:</b> back-button-to-close, background-location
 * routing, and scroll restoration. All three are a router rewrite dressed up as
 * a convenience.</p>
 *
 * <p><b>Focus management is inherited, not absent.</b> This comment used to list
 * a focus trap alongside those three and claim that "`ResourceDrawer` and
 * `Modal` both lack focus management today" — both clauses have been false since
 * 2026-08-02. {@link ResourceDrawer} runs the shared `useFocusTrap` over the
 * drawer panel and `useOverlayDismiss` for stack-aware Escape and the body
 * scroll lock, and `Modal` does the same; the fix landed in the shared
 * primitives, which is exactly where the old note said it belonged. Nothing is
 * needed here.</p>
 */
export interface EntityRecordDrawerProps {
  open: boolean;
  onClose: () => void;

  /** What the record is called. Callers pass the name the ROW already carries,
   *  so the drawer is titled the instant it opens rather than after a fetch. */
  title: string;
  subtitle?: string;

  /** The record's own address — where "Open full page" goes. */
  fullPageHref: string;
  fullPageLabel?: string;

  /** Panel width, a per-instance opt-in. The default (`xl`, max-w-5xl) keeps
   *  the listing and prequal-answers drawers exactly as they were; only a body
   *  that has outgrown it — the submission Overview's vitals strip + rail —
   *  asks for `2xl`. */
  width?: ResourceDrawerProps["width"];

  /** Header controls rendered beside "Open full page" — the record page's own
   *  status control and address links (Layout Standard §7.4a, v1.8). Per
   *  instance and opt-in: absent, the header is title + the link, as before. */
  actions?: ReactNode;

  testId?: string;
  children: ReactNode;
}

export function EntityRecordDrawer({
  open,
  onClose,
  title,
  subtitle,
  fullPageHref,
  fullPageLabel = "Open full page",
  width = "xl",
  actions,
  testId,
  children,
}: EntityRecordDrawerProps) {
  return (
    <ResourceDrawer
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      // `xl` (max-w-5xl) by default — an entity body is a tab strip over a
      // two-column layout, and the narrower widths wrap it into a column of stubs.
      width={width}
      testId={testId}
      headerActions={
        // `PageHeader`'s actions idiom — `min-w-0 flex-wrap justify-end` — so
        // the cluster can wrap onto a second line once the header's own cluster
        // (shrinkable and wrapping since #1204) runs out of row. It does NOT
        // keep the title from collapsing: that column's basis is 0.
        // The Link is always last: the address is the one thing every record
        // drawer promises, and a caller's actions sit to its left.
        <div className="flex min-w-0 flex-wrap items-center justify-end gap-space-2">
          {actions && (
            <div
              className="flex min-w-0 flex-wrap items-center justify-end gap-space-2"
              data-testid={testId ? `${testId}-actions` : undefined}
            >
              {actions}
            </div>
          )}
          {/* A plain link, not a "close then navigate": navigating unmounts the
              host page and the drawer with it, so closing first would only add a
              frame of empty chrome. */}
          <Link
            to={fullPageHref}
            className="flex items-center gap-space-1 text-small text-gold-shadow hover:underline"
            data-testid={testId ? `${testId}-full-page` : undefined}
          >
            <Maximize2 size={13} strokeWidth={1.5} aria-hidden />
            {fullPageLabel}
          </Link>
        </div>
      }
    >
      {children}
    </ResourceDrawer>
  );
}

/**
 * The drawer's open record, kept in ONE search parameter.
 *
 * <p>This is the `?payment=` contract the ten allocation-drawer sites already
 * run on, factored out: the open record is URL state, so a refresh reopens it
 * and the address bar is a shareable pointer to exactly what the reader is
 * looking at — "the listing I am on" rather than "the buyer page, then that
 * row".</p>
 *
 * <p><b>Written with `replace`</b>, like every one of those sites. Opening and
 * closing a drawer is not a place you travelled to; pushing would bury the page
 * a reader actually arrived from under one history entry per glance, and Back
 * would walk them through their own browsing rather than out of it.</p>
 *
 * <p><b>The parameter is deleted on close</b> rather than blanked, so a closed
 * drawer leaves no trace in a copied URL.</p>
 *
 * <p>🔴 <b>This is not the tab parameter.</b> The host page owns `?tab=`; a
 * drawer that wrote to it would fight the page underneath for the same key, and
 * closing the drawer would leave the page on whichever tab the drawer was
 * showing. Tab state inside a drawer is local component state — see the record
 * drawers in the buyer module.</p>
 *
 * <p><b>The tab a drawer OPENS on is local too.</b> An action that names its
 * destination — "Draft intro email" opens a submission on its Buyer intro tab —
 * passes `initialTab` to `open`; the hook holds it in component state and hands
 * it back for the drawer's first render. It is never written to the URL, for the
 * reason above, so a refresh reopens the record on its default tab. That is the
 * honest reading of a glance: the record is the shareable fact, the tab is
 * not.</p>
 */
export function useRecordDrawerParam<TTab extends string = string>(param: string): {
  openId: string | null;
  /** The tab the last `open` asked for, or null — the drawer's default. */
  initialTab: TTab | null;
  open: (id: string, initialTab?: TTab) => void;
  close: () => void;
} {
  const [params, setParams] = useSearchParams();
  const openId = params.get(param);
  const [initialTab, setInitialTab] = useState<TTab | null>(null);

  const open = useCallback(
    (id: string, tab?: TTab) => {
      // Reset on EVERY open: an open that names no tab must not inherit the
      // previous one's.
      setInitialTab(tab ?? null);
      const next = new URLSearchParams(params);
      next.set(param, id);
      setParams(next, { replace: true });
    },
    [param, params, setParams],
  );

  const close = useCallback(() => {
    setInitialTab(null);
    const next = new URLSearchParams(params);
    next.delete(param);
    setParams(next, { replace: true });
  }, [param, params, setParams]);

  return { openId, initialTab, open, close };
}
